import { clsx } from 'clsx';
import React from 'react';
import type { ProviderList } from '../../../../../js/api';
import { SourceTypeEnum } from '../../../../../js/api';
import { StatusPill } from '../../../components/StatusPill';
import { WorkflowScopes } from '../../../constants/workflows';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { formatDateTo24Hr } from '../../../utils/date';
import { formatAdminRatePercent } from '../../../utils/utils';
import { labelsFor } from './providerDisplay';

export const PROVIDER_DATE_FORMAT = 'MMM d, yyyy';

// Shared with the table cells in ProvidersList and the drawer.
export { labelsFor };

/** CodeDefinition code → title maps the card (and the table cells) render labels from. */
export type ProviderCardMaps = {
  degree: Record<string, string>;
  specialty: Record<string, string>;
  disposition: Record<string, string>;
  registration: Record<string, string>;
  source: Record<string, string>;
  schedule: Record<string, string>;
};

export const formatMarketplaceFee = (fee: number | null | undefined): string =>
  fee === null || fee === undefined ? '--' : `$${fee}`;

/** `updated_at`/`created_at` are full ISO timestamps, so the timestamp formatter is used. */
export const formatProviderTimestamp = (timestamp: string | null | undefined): string =>
  formatDateTo24Hr(timestamp, PROVIDER_DATE_FORMAT) || '--';

/** Onboarding step indicator: check when the step is done, dash otherwise. */
export const StepFlag: React.FC<{ done: boolean; label: string }> = ({ done, label }) => (
  <i
    className={done ? 'far fa-check tw:text-green-700' : 'far fa-minus tw:text-zinc-400'}
    aria-label={`${label}: ${done ? 'complete' : 'incomplete'}`}
    title={label}
  />
);

export const ProviderCard: React.FC<{
  provider: ProviderList;
  maps: ProviderCardMaps;
  adminLink?: React.ReactNode;
  isSelected?: boolean;
}> = ({ provider, maps, adminLink, isSelected = false }) => {
  const isOrganizationSourced = provider.source_type === SourceTypeEnum.ORGANIZATION;
  const registrationLabel =
    maps.registration[provider.registration_status] || provider.registration_status || '--';
  const dispositionLabel = provider.profile_disposition
    ? maps.disposition[provider.profile_disposition] || provider.profile_disposition
    : '--';

  return (
    <div
      className={clsx(
        'tw:h-full tw:bg-white tw:rounded-lg tw:shadow-sm tw:border tw:transition-shadow tw:hover:shadow-md',
        isSelected ? 'tw:border-brand-green-0 tw:bg-brand-green-2' : 'tw:border-zinc-200'
      )}
    >
      <div className="tw:p-3">
        <div className="tw:flex tw:items-start tw:gap-2 tw:mb-2">
          {adminLink}
          <p className="tw:!text-[16px] tw:font-semibold tw:text-zinc-900 tw:!mb-0 tw:line-clamp-2">
            {provider.full_name}
          </p>
          {isOrganizationSourced && (
            <span className={SOURCE_TYPE_ORGANIZATION_BADGE_STYLE}>1099</span>
          )}
        </div>
        <div className="tw:mb-1 tw:text-sm tw:text-zinc-600">{provider.public_name}</div>
        <div className="tw:mb-1 tw:text-sm tw:text-zinc-600">
          {labelsFor(provider.degree, maps.degree)} ·{' '}
          {labelsFor(provider.specialties, maps.specialty)}
        </div>
        <div className="tw:mb-1 tw:text-sm tw:text-zinc-600">
          {provider.state || '--'} · {formatAdminRatePercent(provider.admin_rate)} ·{' '}
          {formatMarketplaceFee(provider.marketplace_hourly_fee)}
        </div>
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:mt-2 tw:text-xs tw:text-zinc-700">
          <StatusPill scope={WorkflowScopes.Provider} status={provider.current_status} small />
          <span>{registrationLabel}</span>
          <span>{dispositionLabel}</span>
        </div>
        <div className="tw:flex tw:items-center tw:justify-between tw:mt-2 tw:pt-2 tw:border-t tw:border-zinc-100 tw:text-xs tw:text-zinc-500">
          <div className="tw:flex tw:items-center tw:gap-3">
            <StepFlag done={provider.has_completed_basic_info} label="Basics" />
            <StepFlag done={provider.has_completed_rates} label="Rates" />
            <StepFlag done={provider.has_completed_professional_traits} label="Traits" />
          </div>
          <span>{formatProviderTimestamp(provider.updated_at)}</span>
        </div>
      </div>
    </div>
  );
};

export default ProviderCard;
